import React from 'react';
import { StandardTableActions, Badge, ColoredBadge } from '../../components/ui';
import { Column } from '../../components/DataTable/DataTable';
import { SupportTicket } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import LinkedConversationsSection from '../conversations/components/LinkedConversationsSection';
import ActivityLogSection from '../activity-logs/components/ActivityLogSection';
import { EntityAttachmentsSection } from '../../components/EntityAttachmentsSection';

const renderSupportTicketStatusBadge = (label?: string, color?: string) => (
  <ColoredBadge label={label || '-'} color={color} />
);

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Support Tickets',
    description: 'Manage your support tickets database',
    permissionModuleKey: 'support_ticket',
    route: {
      list: '/support-ticket',
      new: '/support-ticket/new',
      detail: (id: number) => `/support-ticket/${id}`,
      edit: (id: number) => `/support-ticket/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/support-ticket',
      detail: (id: number) => `/support-ticket/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search tickets by title, reference, client, or status...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'title', 'reference', 'clientId', 'assignedToId', 'statusId', 'priority'
      ],
    },
    {
      name: 'details',
      label: 'Details',
      icon: '📋',
      fields: [
        'description', 'supportAgreementId', 'taskId', 'createdAt', 'updatedAt'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'title',
        label: 'Title',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: SupportTicket) => item.title,
        options: [],
      },
      {
        key: 'reference',
        label: 'Reference',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: SupportTicket) => item.reference ? `#${item.reference}` : '',
        options: [],
      },
      {
        key: 'clientId',
        label: 'Client',
        placeholder: 'All Clients',
        type: 'select' as const,
        getValue: (item: SupportTicket) => 
          item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : ''),
        options: [],
      },
      {
        key: 'assignedToId',
        label: 'Assigned To',
        placeholder: 'All Users',
        type: 'select' as const,
        getValue: (item: SupportTicket) => 
          item.assignedTo
            ? (item.assignedTo.userDisplayName || item.assignedTo.userFirstName || `User #${item.assignedTo.id}`)
            : (item.assignedToId ? `User #${item.assignedToId}` : ''),
        options: [],
      },
      {
        key: 'statusId',
        label: 'Status',
        placeholder: 'All Statuses',
        type: 'select' as const,
        getValue: (item: SupportTicket) => 
          item.statusDescr?.pts_descr || (item.statusId ? `Status #${item.statusId}` : ''),
        options: [],
      },
      {
        key: 'priority',
        label: 'Priority',
        placeholder: 'All Priorities',
        type: 'select' as const,
        getValue: (item: SupportTicket) => 
          item.priorityDescr?.p_descr || (item.priority ? `Priority #${item.priority}` : ''),
        options: [],
      },
      {
        key: 'description',
        label: 'Description',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: SupportTicket) => item.description,
        options: [],
      },
      {
        key: 'createdAt',
        label: 'Created At',
        placeholder: 'All Created At',
        type: 'date' as const,
        getValue: (item: SupportTicket) =>
          item.createdAt
            ? formatDateGlobal(item.createdAt)
            : undefined,
        options: [],
      },
      {
        key: 'updatedAt',
        label: 'Updated At',
        placeholder: 'All Updated At',
        type: 'date' as const,
        getValue: (item: SupportTicket) =>
          item.updatedAt
            ? formatDateGlobal(item.updatedAt)
            : undefined,
        options: [],
      },
    ],
    // Filter logic functions
    applyFilter: (
      items: SupportTicket[],
      filters: {
        title: string[] | string;
        reference: string[] | string;
        clientId: string[];
        assignedToId: string[];
        statusId: string[];
        priority: string[];
        description: string[] | string;
        createdAt: string[];
        updatedAt: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Title filter
        const titleFilter = filters.title;
        if (typeof titleFilter === 'string' && titleFilter.trim() !== '') {
          const value = (item.title || '').toLowerCase();
          if (!value.includes(titleFilter.toLowerCase())) return false;
        } else if (Array.isArray(titleFilter) && titleFilter.length > 0) {
          const value = (item.title || '').toLowerCase();
          const matches = titleFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Reference filter
        const referenceFilter = filters.reference;
        const referenceValue = item.reference ? `#${item.reference}` : '';
        if (typeof referenceFilter === 'string' && referenceFilter.trim() !== '') {
          if (!referenceValue.toLowerCase().includes(referenceFilter.toLowerCase())) return false;
        } else if (Array.isArray(referenceFilter) && referenceFilter.length > 0) {
          const matches = referenceFilter.some((filter) => {
            return referenceValue.toLowerCase().includes(filter.toLowerCase()) || 
                   filter.toLowerCase().includes(referenceValue.toLowerCase());
          });
          if (!matches) return false;
        }

        // Client filter
        if (filters.clientId && filters.clientId.length > 0) {
          const displayValue = item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '');
          const matches = filters.clientId.some((filter) => {
            return displayValue === filter || 
                   (item.clientId && filter === `Client #${item.clientId}`) ||
                   (item.client?.clientName && filter === item.client.clientName);
          });
          if (!matches) return false;
        }

        // Assigned To filter
        if (filters.assignedToId && filters.assignedToId.length > 0) {
          const displayValue = item.assignedTo
            ? (item.assignedTo.userDisplayName || item.assignedTo.userFirstName || `User #${item.assignedTo.id}`)
            : (item.assignedToId ? `User #${item.assignedToId}` : '');
          const matches = filters.assignedToId.some((filter) => {
            return displayValue === filter ||
                   (item.assignedToId && filter === `User #${item.assignedToId}`) ||
                   (item.assignedTo?.userDisplayName && filter === item.assignedTo.userDisplayName) ||
                   (item.assignedTo?.id && filter === `User #${item.assignedTo.id}`);
          });
          if (!matches) return false;
        }

        // Status filter
        if (filters.statusId && filters.statusId.length > 0) {
          const displayValue = item.statusDescr?.pts_descr || (item.statusId ? `Status #${item.statusId}` : '');
          const matches = filters.statusId.some((filter) => {
            return displayValue === filter ||
                   (item.statusId && filter === `Status #${item.statusId}`) ||
                   (item.statusDescr?.pts_descr && filter === item.statusDescr.pts_descr);
          });
          if (!matches) return false;
        }

        // Priority filter
        if (filters.priority && filters.priority.length > 0) {
          const displayValue = item.priorityDescr?.p_descr || (item.priority ? `Priority #${item.priority}` : '');
          const matches = filters.priority.some((filter) => {
            return displayValue === filter ||
                   (item.priority && filter === `Priority #${item.priority}`) ||
                   (item.priorityDescr?.p_descr && filter === item.priorityDescr.p_descr);
          });
          if (!matches) return false;
        }

        // Description filter
        const descriptionFilter = filters.description;
        if (typeof descriptionFilter === 'string' && descriptionFilter.trim() !== '') {
          const value = (item.description || '').toLowerCase();
          if (!value.includes(descriptionFilter.toLowerCase())) return false;
        } else if (Array.isArray(descriptionFilter) && descriptionFilter.length > 0) {
          const value = (item.description || '').toLowerCase();
          const matches = descriptionFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Created At filter
        if (filters.createdAt.length > 0) {
          if (ranges.createdAt) {
            const range = ranges.createdAt;
            if (range.start && range.end && item.createdAt) {
              const itemDate = new Date(item.createdAt);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.createdAt
              ? formatDateGlobal(item.createdAt)
              : '';
            const matches = filters.createdAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Updated At filter
        if (filters.updatedAt.length > 0) {
          if (ranges.updatedAt) {
            const range = ranges.updatedAt;
            if (range.start && range.end && item.updatedAt) {
              const itemDate = new Date(item.updatedAt);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.updatedAt
              ? formatDateGlobal(item.updatedAt)
              : '';
            const matches = filters.updatedAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        return true;
      });
    },
  },

  // Toolbar Actions Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-csv',
            label: 'Export to CSV',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToCSV } = await import('../../services/exportService');
                await exportToCSV(exportData);
              } catch (error: any) {
                console.error('Failed to export to CSV:', error);
                throw new Error(error?.message || 'Failed to export to CSV. Please check the console for details.');
              }
            },
          },
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
          {
            key: 'export-pdf',
            label: 'Export to PDF',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToPDF } = await import('../../utils/exportToPdf');
                await exportToPDF(exportData);
              } catch (error: any) {
                console.error('Failed to export to PDF:', error);
                throw new Error(error?.message || 'Failed to export to PDF. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Support Ticket',
        showTitle: false,
        variant: 'primary' as const,
        href: '/support-ticket/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: SupportTicket) => {
      window.location.href = `/support-ticket/${item.id}`;
    },
    keyExtractor: (item: SupportTicket) => item.id,
    getClientId: (item: SupportTicket) => item.clientId ?? null,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new support ticket.',
    noSearchResults: 'No support tickets found matching your search criteria.',
    noFilterResults: 'No support tickets match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Support Ticket',
    message: 'Are you sure you want to delete this support ticket? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete support ticket. Please try again.',
    handler: async (id: number) => {
      const { deleteSupportTicket } = await import('../../services/mutations');
      await deleteSupportTicket(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Support Ticket Details',
    notFoundTitle: 'Support Ticket not found',
    notFoundMessage: 'The support ticket you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Support Tickets',
    defaultTitle: 'Unnamed Support Ticket',
    getTitle: (item: SupportTicket) => {
      return item.title || (item.reference ? `Ticket #${item.reference}` : `Ticket #${item.id}`);
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Support Ticket',
      subtitle: 'Add a new support ticket to your database',
      submitText: 'Create Support Ticket',
    },
    edit: {
      title: 'Edit Support Ticket',
      submitText: 'Update Support Ticket',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading support ticket...',
    errorMessage: 'Failed to save support ticket. Please try again.',
    supportsFiles: true,
    create: async (data: Partial<SupportTicket>, files?: File[], filesToDetach?: number[]) => {
      const { createSupportTicket } = await import('../../services/mutations');
      return await createSupportTicket(data, files, filesToDetach);
    },
    update: async (id: number, data: Partial<SupportTicket>, files?: File[], filesToDetach?: number[]) => {
      const { updateSupportTicket } = await import('../../services/mutations');
      return await updateSupportTicket(id, data, files, filesToDetach);
    },
    getViewFileUrl: (entityId: number, fileId: number) =>
      `/support-ticket/${entityId}/files/${fileId}`,
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      required: true,
      placeholder: 'Select a client',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/crm-clients', // API endpoint to fetch all client options
      labelField: 'clientName', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'supportAgreementId',
      label: 'Support Agreement',
      type: 'select',
      placeholder: 'Select a support agreement (optional)',
      options: [], // Will be populated dynamically in Form.tsx
    },
    {
      name: 'taskId',
      label: 'Task',
      type: 'select',
      placeholder: 'Select a task (optional)',
      options: [], // Will be populated dynamically in Form.tsx
    },
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Enter ticket title',
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255,
      },
    },
    {
      name: 'assignedToId',
      label: 'Assigned To',
      type: 'select',
      required: true,
      placeholder: 'Select a user',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/users', // API endpoint to fetch all user options
      labelField: 'userDisplayName', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'statusId',
      label: 'Status',
      type: 'select',
      required: true,
      placeholder: 'Select status',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/p-crm-ticket-status', // API endpoint to fetch all ticket status options
      labelField: 'pts_descr', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'priority',
      label: 'Priority',
      type: 'select',
      placeholder: 'Select priority (optional)',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/p-crm-priority', // API endpoint to fetch all priority options
      labelField: 'p_descr', // Field name in API response to use as label
    },
    {
      name: 'reference',
      label: 'Reference',
      type: 'text',
      placeholder: 'Auto-generated',
      disabled: true,
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Enter ticket description',
      rows: 4,
      validation: {
        maxLength: 2000,
      },
    },
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        icon: '📋',
        gridCols: 'md:grid-cols-12',
        fields: ['clientId', 'supportAgreementId', 'taskId', 'title'],
        fieldSpans: {
          clientId: 'md:col-span-6',
          supportAgreementId: 'md:col-span-6',
          taskId: 'md:col-span-6',
          title: 'md:col-span-12',
        },
        collapsible: true,
      },
      {
        id: 'assignment',
        title: 'Assignment & Status',
        icon: '👤',
        gridCols: 'md:grid-cols-2',
        fields: ['assignedToId', 'statusId', 'priority', 'reference'],
        collapsible: true,
      },
      {
        id: 'details',
        title: 'Details',
        icon: '📝',
        gridCols: 'md:grid-cols-12',
        fields: ['description'],
        fieldSpans: {
          description: 'md:col-span-12',
        },
        collapsible: true,
      },
    ],
  } as FormLayout,
};

// Helper function to get columns
export const getColumns = (formatDate: (dateString?: string) => string): Column<SupportTicket>[] => [
  {
    key: 'reference',
    header: 'Reference',
    sortable: true,
    render: (value) => (
      value ? `#${value}` : '-'
    ),
  },
  {
    key: 'title',
    header: 'Title',
    sortable: true,
    render: (value, row) => {
      const clientLine = row.client?.clientName || (row.clientId ? `Client #${row.clientId}` : null);
      if (!clientLine) return value || '-';
      return (
        <div className="flex flex-col min-w-0">
          <span className="truncate">{value || '-'}</span>
          <span className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">{clientLine}</span>
        </div>
      );
    },
  },
  {
    key: 'assignedToId',
    header: 'Assigned To',
    sortable: true,
    render: (value, row) => (
      row.assignedTo
        ? (row.assignedTo.userDisplayName || row.assignedTo.userFirstName || `User #${row.assignedTo.id}`)
        : (value ? `User #${value}` : '-')
    ),
  },
  {
    key: 'statusId',
    header: 'Status',
    sortable: true,
    render: (value, row) => {
      const status = row.statusDescr?.pts_descr || value || '-';
      if (!status || status === '-') return <span className="text-sm text-gray-500">-</span>;
      return renderSupportTicketStatusBadge(status, row.statusDescr?.pts_color);
    },
  },
  {
    key: 'priority',
    header: 'Priority',
    sortable: true,
    render: (value, row) => (
      row.priorityDescr?.p_descr || (value ? `Priority #${value}` : '-')
    ),
  },
  {
    key: 'createdAt',
    header: 'Created At',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'FileAttachment',
    header: 'Attachments',
    sortable: false,
    render: (_value, row) => {
      const count = row.FileAttachment?.length ?? 0;
      return count > 0 ? `${count} file${count !== 1 ? 's' : ''}` : '-';
    },
  },
];

// Helper function to get detail sections
export const getDetailSections = (
  item: SupportTicket,
  formatDate: (dateString?: string) => string
) => {
  const status = item.statusDescr?.pts_descr || '';

  return [
    {
      title: 'Basic Information',
      icon: '📋',
      fields: [
        {
          label: 'Reference',
          value: item.reference ? `#${item.reference}` : '-',
        },
        {
          label: 'Title',
          value: item.title || '-',
        },
        {
          label: 'Client',
          value: item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : 'None'),
        },
        {
          label: 'Assigned To',
          value: item.assignedTo
            ? (item.assignedTo.userDisplayName || item.assignedTo.userFirstName || `User #${item.assignedTo.id}`)
            : (item.assignedToId ? `User #${item.assignedToId}` : 'Unassigned'),
        },
        {
          label: 'Status',
          value: status || '-',
          render: status ? renderSupportTicketStatusBadge(status, item.statusDescr?.pts_color) : '-',
        },
        {
          label: 'Priority',
          value: item.priorityDescr?.p_descr || (item.priority ? `Priority #${item.priority}` : '-'),
        },
      ],
    },
    {
      title: 'Details',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: item.description || '-',
        },
        {
          label: 'Support Agreement',
          value: item.supportAgreement
            ? (item.supportAgreement.description || `${item.supportAgreement.agreementType || ''} - ${item.supportAgreement.id}`.trim() || `Agreement #${item.supportAgreement.id}`)
            : 'None',
        },
        {
          label: 'Task',
          value: item.task
            ? (item.task.taskDescription || item.task.taskSubject || `Task #${item.task.taskId}`)
            : 'None',
        },
        {
          label: 'Created At',
          value: formatDate(item.createdAt),
        },
        {
          label: 'Updated At',
          value: formatDate(item.updatedAt),
        },
        {
          label: 'Created By',
          value: item.createdBy?.userFirstName || (item.createdById ? `User #${item.createdById}` : '-'),
        },
        {
          label: 'Updated By',
          value: item.updatedBy?.userFirstName || (item.updatedById ? `User #${item.updatedById}` : '-'),
        },
      ],
    },
    {
      title: 'Attachments',
      icon: '📎',
      fields: [
        {
          label: '',
          value: React.createElement(EntityAttachmentsSection, {
            downloadEndpoint: `${moduleConfig.module.api.detail(item.id)}/files`,
            files: item.FileAttachment || [],
            defaultDownloadName: `ticket_${item.id}_files.zip`,
            getViewFileUrl: (fileId) => `${moduleConfig.module.api.detail(item.id)}/files/${fileId}`,
          }),
        },
      ],
    },
    {
      title: 'Conversations',
      icon: '💬',
      fields: [
        {
          label: '',
          value: React.createElement(LinkedConversationsSection, { ticketId: item.id }),
        },
      ],
    },
    {
      title: 'Activity Log',
      icon: '📋',
      fields: [
        {
          label: '',
          value: React.createElement(ActivityLogSection, { entityType: 'Ticket', entityId: item.id }),
        },
      ],
    },
  ];
};

// Helper function to get status badges
export const getStatusBadges = (item: SupportTicket) => {
  const status = item.statusDescr?.pts_descr || '';

  return (
    <>
      {status && (
        renderSupportTicketStatusBadge(status, item.statusDescr?.pts_color)
      )}
      {item.priorityDescr && (
        <Badge variant="info">
          Priority: {item.priorityDescr.p_descr}
        </Badge>
      )}
    </>
  );
};

// Helper function to get initial form data
export const getInitialFormData = (): Partial<SupportTicket> => {
  return {
    title: '',
    clientId: undefined,
    assignedToId: undefined,
    statusId: undefined,
    priority: undefined,
    description: '',
    supportAgreementId: undefined,
    taskId: undefined,
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: SupportTicket): Partial<SupportTicket> => {
  return {
    title: item.title || '',
    clientId: item.clientId,
    assignedToId: item.assignedToId,
    statusId: item.statusId,
    priority: item.priority,
    description: item.description || '',
    supportAgreementId: item.supportAgreementId,
    taskId: item.taskId,
    reference: item.reference,
  };
};

// Helper function to render toolbar icon
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    case 'kanban':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to render table action buttons
export const getTableActions = (
  item: SupportTicket,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

export type ModuleConfig = typeof moduleConfig;

